import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import WalkingPerson from './WalkingPerson';
import '../styles/Transportation.css';
import { useAuth } from '../context/AuthContext'; // ✅ Import AuthContext

export default function Transportation() {
    const [origin, setOrigin] = useState('');
    const [destination, setDestination] = useState('');
    const [transportOptions, setTransportOptions] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [userLocation, setUserLocation] = useState(null);
    const navigate = useNavigate();
    const { fetchWithAuth } = useAuth(); // ✅ Use auth fetch
    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        checkAuth();
        getCurrentLocation();
    }, []);

    // ------------------ Auth check ------------------
    const checkAuth = async () => {
        try {
            const res = await fetchWithAuth(`${API_URL}/check-auth`, { method: 'GET' });
            const data = await res.json();
            if (!data.loggedIn) {
                navigate('/login');
            }
        } catch (err) {
            console.error(err);
            navigate('/login');
        }
    };

    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    setUserLocation({ lat: latitude, lon: longitude });

                    try {
                        const response = await fetch(
                            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
                        );
                        const data = await response.json();
                        const locationName = data.display_name
                            .split(',')
                            .slice(0, 2)
                            .join(', ');
                        setOrigin(locationName);
                    } catch (err) {
                        console.error('Error getting location name:', err);
                    }
                },
                (error) => {
                    console.error('Error getting location:', error);
                }
            );
        }
    };

    const searchTransportation = async (e) => {
        e.preventDefault();
        if (!origin || !destination) {
            setError('Please enter both origin and destination');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const originCoords = await getCoordinates(origin);
            const destCoords = await getCoordinates(destination);

            if (!originCoords || !destCoords) {
                setError('Unable to find coordinates for the locations');
                setLoading(false);
                return;
            }

            const res = await fetchWithAuth(
                `${API_URL}/get-transportation?origin=${originCoords.lon},${originCoords.lat}&destination=${destCoords.lon},${destCoords.lat}`,
                { method: 'GET' }
            );

            if (res.status === 401) {
                navigate('/login');
                return;
            }

            const data = await res.json();
            if (data.transportOptions) {
                setTransportOptions(data.transportOptions);
            } else {
                setError('No transportation options found');
            }
        } catch (err) {
            console.error('Error fetching transportation:', err);
            setError('Failed to fetch transportation options');
        } finally {
            setLoading(false);
        }
    };

    const getCoordinates = async (locationName) => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationName)}&limit=1`
            );
            const data = await response.json();
            if (data.length > 0) {
                return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
            }
            return null;
        } catch (err) {
            console.error('Error geocoding:', err);
            return null;
        }
    };

    const handleBookTransport = (option) => {
        if (option.bookingUrl) {
            window.open(option.bookingUrl, '_blank');
        }
    };

    const getTransportIcon = (mode) => {
        switch (mode) {
            case 'driving': return '🚗';
            case 'transit': return '🚌';
            case 'walking': return '🚶';
            case 'cycling': return '🚴';
            case 'flight': return '✈️';
            default: return '🚗';
        }
    };

    return (
        <div className="transportation-container">
            <Navbar />

            <div className="transportation-content">
                <div className="transportation-hero">
                    <div className="hero-background">
                        <img
                            src="https://images.unsplash.com/photo-1597367624241-071ab9d6b9e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxzY2VuaWN8ZW58MHx8fHwxNzU2NzIzNTE2fDA&ixlib=rb-4.1.0&q=85"
                            alt="Transportation"
                            className="hero-image"
                        />
                        <div className="hero-overlay"></div>
                    </div>
                    <div className="hero-content">
                        <h1>Find Your Perfect Route</h1>
                        <p>Discover the best transportation options for your journey</p>
                    </div>
                </div>

                <div className="search-section">
                    <form className="transport-search-form" onSubmit={searchTransportation}>
                        <div className="search-inputs">
                            <div className="input-group">
                                <label>From</label>
                                <input
                                    type="text"
                                    placeholder="Enter origin location"
                                    value={origin}
                                    onChange={(e) => setOrigin(e.target.value)}
                                />
                            </div>

                            <div className="search-divider">
                                <button
                                    type="button"
                                    className="swap-locations"
                                    onClick={() => {
                                        const temp = origin;
                                        setOrigin(destination);
                                        setDestination(temp);
                                    }}
                                >
                                    ⇄
                                </button>
                            </div>

                            <div className="input-group">
                                <label>To</label>
                                <input
                                    type="text"
                                    placeholder="Enter destination"
                                    value={destination}
                                    onChange={(e) => setDestination(e.target.value)}
                                />
                            </div>
                        </div>

                        <button type="submit" className="search-transport-btn">
                            Search Transportation
                        </button>
                    </form>
                </div>

                {error && <div className="error-message">{error}</div>}

                {loading && (
                    <div className="loading-container">
                        <WalkingPerson />
                        <p>Finding the best routes for you...</p>
                    </div>
                )}

                {transportOptions && (
                    <div className="transport-results">
                        <h2>Available Transportation Options</h2>
                        <div className="transport-options-grid">
                            {Object.entries(transportOptions).map(([key, option]) => (
                                <div key={key} className="transport-option-card">
                                    <div className="transport-header">
                                        <div className="transport-icon">
                                            {getTransportIcon(option.mode)}
                                        </div>
                                        <div className="transport-info">
                                            <h3>{option.mode.charAt(0).toUpperCase() + option.mode.slice(1)}</h3>
                                            <div className="transport-details">
                                                <span className="duration">⏱️ {option.duration} min</span>
                                                <span className="distance">📏 {option.distance} km</span>
                                                <span className="cost">💰 ${option.estimatedCost}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="transport-actions">
                                        <button
                                            className="book-transport-btn"
                                            onClick={() => handleBookTransport(option)}
                                        >
                                            Book Now
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Popular Routes Section */}
                <div className="popular-routes">
                    <h2>Popular Routes</h2>
                    <div className="routes-grid">
                        <div className="route-card">
                            <div className="route-info">
                                <h3>Mumbai → Goa</h3>
                                <p>Multiple daily flights and buses</p>
                                <span className="route-time">1h 30m by flight</span>
                            </div>
                            <button
                                className="quick-search-btn"
                                onClick={() => {
                                    setOrigin('Mumbai');
                                    setDestination('Goa');
                                }}
                            >
                                Search
                            </button>
                        </div>

                        <div className="route-card">
                            <div className="route-info">
                                <h3>Delhi → Manali</h3>
                                <p>Scenic bus journey through mountains</p>
                                <span className="route-time">12h by bus</span>
                            </div>
                            <button
                                className="quick-search-btn"
                                onClick={() => {
                                    setOrigin('Delhi');
                                    setDestination('Manali');
                                }}
                            >
                                Search
                            </button>
                        </div>

                        <div className="route-card">
                            <div className="route-info">
                                <h3>Bangalore → Chennai</h3>
                                <p>High-speed trains and flights</p>
                                <span className="route-time">5h by train</span>
                            </div>
                            <button
                                className="quick-search-btn"
                                onClick={() => {
                                    setOrigin('Bangalore');
                                    setDestination('Chennai');
                                }}
                            >
                                Search
                            </button>
                        </div>
                    </div>
                </div>

                {/* Transportation Tips */}
                <div className="transport-tips">
                    <h2>Travel Tips</h2>
                    <div className="tips-grid">
                        <div className="tip-card">
                            <div className="tip-icon">✈️</div>
                            <h3>Book Flights Early</h3>
                            <p>Save up to 50% by booking flights 2-3 months in advance</p>
                        </div>
                        <div className="tip-card">
                            <div className="tip-icon">🚂</div>
                            <h3>Train Travel</h3>
                            <p>Most comfortable for long distances with scenic views</p>
                        </div>
                        <div className="tip-card">
                            <div className="tip-icon">🚌</div>
                            <h3>Bus Options</h3>
                            <p>Budget-friendly with multiple operators and timings</p>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}